import axios, { AxiosInstance } from 'axios'
import type { CartItem, ShippingAddress, ShopOrderResult } from '@/data/shop'
import {
  User,
  Cottage,
  Post,
  Comment,
  Document,
  Payment,
  Notification,
  CreatePostData,
  CreateCommentData,
  CreateDocumentData,
  ApiResponse,
  AssociationEvent,
  EventRSVP,
  CreateEventData,
  RSVPInput,
  DirectoryMember,
  Photo,
  PhotoYear,
  MaintenanceEntry,
  CreateMaintenanceInput,
  ConversationSummary,
  ConversationDetail,
  MessageItem,
  CottageRequest,
  MemberProfile,
} from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabaseClient'

/** One turn in a Help Desk conversation. */
export interface HelpDeskChatMessage {
  role: 'user' | 'assistant'
  content: string
}

/** A cited web source returned when the assistant used live search. */
export interface HelpDeskSource {
  title: string
  url: string
}

/** A reusable draft (bulletin post, email, checklist, …) the member can copy. */
export interface HelpDeskArtifact {
  title: string
  content: string
}

/** The backend's Help Desk reply envelope (already unwrapped). */
export interface HelpDeskReply {
  message: string
  provider: string
  model: string
  usedWebSearch: boolean
  usedLiveData?: boolean
  sources: HelpDeskSource[]
  artifact?: HelpDeskArtifact | null
}

export type FamilyRelationship = 'SPOUSE' | 'CHILD' | 'PARENT' | 'SIBLING' | 'OTHER'
export type FamilyInviteStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED'

/** An invite as seen by the inviting member (includes the shareable token). */
export interface FamilyInvite {
  id: string
  token: string
  invitedEmail: string
  invitedFirstName: string
  invitedLastName: string
  relationship: FamilyRelationship
  message: string | null
  status: FamilyInviteStatus
  cottageNumber: string | null
  createdAt: string
  expiresAt: string
  acceptedAt: string | null
}

/** Safe invite details shown on the public accept page. */
export interface PublicFamilyInvite {
  valid: boolean
  status: FamilyInviteStatus
  invitedEmail: string
  invitedFirstName: string
  invitedLastName: string
  relationship: FamilyRelationship
  cottageNumber: string | null
  message: string | null
  inviterName: string
  expiresAt: string
}

export interface AcceptInviteResult {
  existingAccount: boolean
  hasPassword: boolean
  email: string
}

class ApiService {
  async submitShopOrder(items: CartItem[], shipping: ShippingAddress, requestId: string): Promise<ShopOrderResult> {
    const { data } = await this.api.post('/shop/orders', { items, shipping, requestId })
    return data.data
  }

  private api: AxiosInstance
  private baseURL: string

  constructor() {
    this.baseURL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api/v1'
    
    // 30s timeout because the backend can cold-start (Docker container boot
     // + `npx prisma migrate deploy` + ts-node TypeScript compilation) for
     // 15-25s after idle. The previous 10s timeout guaranteed that the very
     // first request after a cold backend would ECONNABORTED, leaving the
     // auth-callback page stuck on the spinner with no retry path. Photo
     // uploads override this to 60s — see uploadPhoto below.
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor — attach the Supabase access token (if any).
    // Reads from the auth store first; falls back to Supabase directly so we
    // survive the race where the very first request after sign-in fires
    // before onAuthStateChange has hydrated the store. Without the fallback,
    // the first /auth/me on a fresh origin (empty localStorage → magic-link
    // verifyOtp → refreshProfile) goes out unauthenticated and 401s.
    this.api.interceptors.request.use(async (config) => {
      let token = this.getAccessToken()
      if (!token) {
        try {
          const { data } = await supabase.auth.getSession()
          token = data.session?.access_token ?? null
        } catch {
          // Anonymous calls are allowed for public endpoints; fall through.
        }
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor — let the auth store handle 401/403 by reacting
    // to Supabase auth events. We do NOT hard-redirect here; the router
    // (ProtectedRoute) reads store state and routes appropriately.
    this.api.interceptors.response.use((response) => response, (error) => Promise.reject(error))
  }

  /**
   * Public — does this email belong to an active member of the association?
   * Used by SignupPage before kicking off a magic-link send. Backend uses
   * the service-role Supabase client to query `shorewood_allowlist`.
   */
  async checkEligibility(email: string): Promise<{ eligible: boolean }> {
    const response = await this.api.post<{ eligible: boolean }>('/auth/eligibility', { email })
    return response.data
  }

  /**
   * Password-first signup for an allowlisted member. The backend creates a
   * confirmed Supabase auth user with the chosen password (no email sent).
   * Throws on 403 (not on the allowlist) or 409 (account already exists).
   */
  async registerMember(input: {
    email: string
    password: string
    firstName: string
    lastName: string
  } | string, password?: string): Promise<void> {
    await this.api.post('/auth/register', typeof input === 'string' ? { email: input, password } : input)
  }

  // Auth methods - Note: Login is now handled by Supabase in authStore
  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/auth/me')
    return response.data.data!
  }

  /**
   * Lightweight directory feed: every active member's name, email, cottage,
   * and current in-residence state.
   */
  async getDirectory(): Promise<DirectoryMember[]> {
    const response = await this.api.get<ApiResponse<DirectoryMember[]>>('/users/directory')
    return response.data.data ?? []
  }

  /**
   * Toggle the "I'm at the lake" presence flag. Pass `true` to mark in
   * residence (default 7-day window), `false` to clear.
   */
  async setInResidence(active: boolean, days?: number): Promise<User> {
    const body: Record<string, unknown> = { inResidence: active }
    if (active && typeof days === 'number') body['inResidenceDays'] = days
    const response = await this.api.put<ApiResponse<User>>('/users/profile', body)
    return response.data.data!
  }

  /**
   * Save the "auto-detect At The Lake" preference. `true`/`false` come from
   * the one-time prompt or the /profile presence card.
   */
  async setAutoAtLake(enabled: boolean): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>('/users/profile', {
      autoInResidence: enabled,
    })
    return response.data.data!
  }

  /**
   * Geolocation-driven presence refresh. The server decides whether this
   * actually moves `inResidenceUntil` (48h rolling window, never shortening,
   * no-op unless the member opted in and hasn't manually toggled off) — so
   * calling it when nothing should change is harmless.
   */
  async refreshAtLakePresence(): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>('/users/profile', {
      inResidenceAuto: true,
    })
    return response.data.data!
  }

  // User methods
  /**
   * Update the signed-in member's profile. Only the fields the backend
   * `userController.updateProfile` honors are accepted here — the controller
   * silently drops anything else (email/role/cottage are not user-editable).
   */
  async updateProfile(userData: {
    firstName?: string
    lastName?: string
    avatarUrl?: string | null
    phone?: string | null
    /** Free-form bio shown on /members/:id. ≤500 chars; backend rejects more. */
    bio?: string | null
    inResidence?: boolean
    inResidenceDays?: number
    /** Auto-detect At The Lake preference; null resets to "never asked". */
    autoInResidence?: boolean | null
    isShareholder?: boolean
    assessmentPaidYear?: number | null
    /**
     * ISO timestamp set by <TutorialTour /> on Finish or Skip.
     * Send `null` only from admin tooling (resets the flag).
     */
    tutorialCompletedAt?: string | null
  }): Promise<User> {
    // Backend returns the standard `{ success, data }` envelope (see authController
    // and userController). Unwrap it here so callers always get a User.
    const response = await this.api.put<ApiResponse<User>>('/users/profile', userData)
    return response.data.data!
  }

  /**
   * Upload a profile avatar through the backend. The server uploads to
   * Supabase storage using the service-role key, which bypasses RLS and
   * bucket-level mime/size checks — so any active member can update their
   * avatar regardless of session quirks or browser content-type oddities.
   */
  async uploadAvatar(file: File): Promise<{ url: string; user: User }> {
    const form = new FormData()
    form.append('file', file)
    const response = await this.api.post<ApiResponse<{ url: string; user: User }>>(
      '/users/me/avatar',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.data!
  }

  // Password changes are handled directly on the client via
  // supabase.auth.updateUser (see ProfilePage Security tab) — an authenticated
  // session is the proof of identity, so no current-password step is needed.

  // ----- Cottages -----

  /**
   * Lightweight list of cottages. Used to map between static directory
   * data (keyed by cottageNumber) and the Prisma row IDs needed by
   * maintenance, payments, and photo features.
   */
  async getCottages(): Promise<Array<Pick<Cottage, 'id' | 'number' | 'name' | 'type' | 'status'>>> {
    const response = await this.api.get<ApiResponse<Array<Pick<Cottage, 'id' | 'number' | 'name' | 'type' | 'status'>>>>('/cottages')
    return response.data.data ?? []
  }

  async getCottage(id: string): Promise<Cottage> {
    const response = await this.api.get<ApiResponse<Cottage>>(`/cottages/${id}`)
    return response.data.data!
  }

  // ----- Cottage membership requests -----

  /**
   * Member: file a request to be linked to a cottage. Returns 409 if the
   * caller is already linked or has a PENDING request.
   */
  async createCottageRequest(input: { cottageId: string; note?: string }): Promise<CottageRequest> {
    const response = await this.api.post<ApiResponse<CottageRequest>>('/cottage-requests', input)
    return response.data.data!
  }

  /**
   * Member: my most recent cottage request (any status), or null if none.
   */
  async getMyCottageRequest(): Promise<CottageRequest | null> {
    const response = await this.api.get<ApiResponse<CottageRequest | null>>('/cottage-requests/mine')
    return response.data.data ?? null
  }

  /**
   * Member: cancel my PENDING request. 404 if none exists.
   */
  async cancelMyCottageRequest(): Promise<void> {
    await this.api.delete('/cottage-requests/mine')
  }

  /**
   * Admin: list requests by status. 'PENDING' (default) or 'DECIDED'
   * (APPROVED + REJECTED, capped at 30, decided desc).
   */
  async listCottageRequests(status: 'PENDING' | 'DECIDED' = 'PENDING'): Promise<CottageRequest[]> {
    const response = await this.api.get<ApiResponse<CottageRequest[]>>(
      `/cottage-requests?status=${status}`
    )
    return response.data.data ?? []
  }

  /** Admin: approve. Sets the user's cottage in the same transaction. */
  async approveCottageRequest(id: string): Promise<CottageRequest> {
    const response = await this.api.post<ApiResponse<CottageRequest>>(`/cottage-requests/${id}/approve`)
    return response.data.data!
  }

  /** Admin: reject with optional reviewer note (surfaced to the requester). */
  async rejectCottageRequest(id: string, reviewerNote?: string): Promise<CottageRequest> {
    const response = await this.api.post<ApiResponse<CottageRequest>>(
      `/cottage-requests/${id}/reject`,
      { reviewerNote }
    )
    return response.data.data!
  }

  // Post / bulletin methods (backend mounts these under /bulletin).
  // Every controller wraps responses in `{ success, data }`. Unwrap here so
  // callers can use the typed shape directly.

  async getPosts(): Promise<{
    posts: Post[]
    pagination: { page: number; limit: number; total: number; pages: number }
  }> {
    const response = await this.api.get<ApiResponse<{
      posts: Post[]
      pagination: { page: number; limit: number; total: number; pages: number }
    }>>('/bulletin')
    return response.data.data!
  }

  async getPost(id: string): Promise<Post> {
    const response = await this.api.get<ApiResponse<Post>>(`/bulletin/${id}`)
    return response.data.data!
  }

  async createPost(data: CreatePostData): Promise<Post> {
    const response = await this.api.post<ApiResponse<Post>>('/bulletin', data)
    return response.data.data!
  }

  async updatePost(id: string, data: Partial<CreatePostData>): Promise<Post> {
    const response = await this.api.put<ApiResponse<Post>>(`/bulletin/${id}`, data)
    return response.data.data!
  }

  async deletePost(id: string): Promise<void> {
    await this.api.delete(`/bulletin/${id}`)
  }

  async likePost(id: string): Promise<void> {
    // Backend uses a single toggle endpoint
    await this.api.post(`/bulletin/${id}/like`)
  }

  /**
   * Record that the signed-in member has actually seen these posts (they
   * were visible in the viewport — see usePostViewTracker). Idempotent on
   * the backend, so re-sending an ID is harmless.
   */
  async recordPostViews(postIds: string[]): Promise<void> {
    await this.api.post('/bulletin/views', { postIds })
  }

  // Comment methods
  async addComment(postId: string, data: CreateCommentData): Promise<Comment> {
    const response = await this.api.post<ApiResponse<Comment>>(`/bulletin/${postId}/comments`, data)
    return response.data.data!
  }

  async updateComment(postId: string, commentId: string, data: Partial<CreateCommentData>): Promise<Comment> {
    const response = await this.api.put<ApiResponse<Comment>>(`/bulletin/${postId}/comments/${commentId}`, data)
    return response.data.data!
  }

  async deleteComment(postId: string, commentId: string): Promise<void> {
    await this.api.delete(`/bulletin/${postId}/comments/${commentId}`)
  }

  /**
   * Host a file for a bulletin post — a photo or a document. Returns the public
   * URL to drop into `Post.attachments`, plus the metadata the composer shows
   * while the post is still being written.
   *
   * Distinct from `uploadDocument()`: this writes to the public `bulletin`
   * bucket and creates no Document record.
   */
  async uploadBulletinAttachment(
    formData: FormData,
  ): Promise<{ url: string; name: string; mimeType: string; size: number }> {
    const response = await this.api.post<
      ApiResponse<{ url: string; name: string; mimeType: string; size: number }>
    >('/bulletin/attachments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data!
  }

  // Document methods. Controllers wrap responses in `{ success, data }`.

  async getDocuments(): Promise<{
    documents: Document[]
    pagination: { page: number; limit: number; total: number; pages: number }
  }> {
    const response = await this.api.get<ApiResponse<{
      documents: Document[]
      pagination: { page: number; limit: number; total: number; pages: number }
    }>>('/documents')
    return response.data.data!
  }

  async getDocument(id: string): Promise<Document> {
    const response = await this.api.get<ApiResponse<Document>>(`/documents/${id}`)
    return response.data.data!
  }

  /**
   * Upload a document. The controller responds with the saved Document
   * augmented with `fileUrl` and `url` at the top level so callers that
   * only need a hosted URL (e.g. inline bulletin image attachments) don't
   * have to know the full Document shape.
   */
  async uploadDocument(formData: FormData): Promise<Document & { fileUrl: string; url: string }> {
    const response = await this.api.post<ApiResponse<Document & { fileUrl: string; url: string }>>(
      '/documents/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data!
  }

  async createDocument(data: CreateDocumentData): Promise<Document> {
    const response = await this.api.post<ApiResponse<Document>>('/documents', data)
    return response.data.data!
  }

  async updateDocument(id: string, data: Partial<CreateDocumentData>): Promise<Document> {
    const response = await this.api.put<ApiResponse<Document>>(`/documents/${id}`, data)
    return response.data.data!
  }

  async deleteDocument(id: string): Promise<void> {
    await this.api.delete(`/documents/${id}`)
  }

  /**
   * Get a short-lived signed download URL plus the original file name. The
   * caller is expected to navigate / link to the URL; the backend does not
   * stream the file bytes through this endpoint.
   */
  async getDocumentDownload(id: string): Promise<{ downloadUrl: string; fileName: string }> {
    const response = await this.api.get<ApiResponse<{ downloadUrl: string; fileName: string }>>(
      `/documents/${id}/download`
    )
    return response.data.data!
  }

  // ----- Payments -----

  /**
   * List payments. Members see their own, treasurer sees all. Wraps the
   * `{ success, data: { data, pagination, summary } }` envelope.
   */
  async getPayments(opts: {
    status?: string
    type?: string
    year?: number
    userId?: string
    cottageId?: string
    page?: number
    limit?: number
  } = {}): Promise<{
    data: Payment[]
    pagination: { page: number; limit: number; total: number; pages: number }
    summary: Record<string, { count: number; amount: number }>
  }> {
    const params = new URLSearchParams()
    if (opts.status) params.set('status', opts.status)
    if (opts.type) params.set('type', opts.type)
    if (opts.year) params.set('year', String(opts.year))
    if (opts.userId) params.set('userId', opts.userId)
    if (opts.cottageId) params.set('cottageId', opts.cottageId)
    if (opts.page) params.set('page', String(opts.page))
    if (opts.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    const response = await this.api.get<ApiResponse<{
      data: Payment[]
      pagination: { page: number; limit: number; total: number; pages: number }
      summary: Record<string, { count: number; amount: number }>
    }>>(qs ? `/payments?${qs}` : '/payments')
    return response.data.data!
  }

  async getPayment(id: string): Promise<Payment> {
    const response = await this.api.get<ApiResponse<Payment>>(`/payments/${id}`)
    return response.data.data!
  }

  async createPayment(data: {
    userId: string
    amount: number
    type: string
    description?: string
    dueDate?: string
    cottageId?: string
  }): Promise<Payment> {
    const response = await this.api.post<ApiResponse<Payment>>('/payments', data)
    return response.data.data!
  }

  async bulkCreatePayments(data: {
    amount: number
    type: string
    description?: string
    dueDate?: string
    userIds?: string[]
  }): Promise<{ created: Array<{ userId: string; paymentId: string }>; skipped: Array<{ userId: string; reason: string }> }> {
    const response = await this.api.post<ApiResponse<{
      created: Array<{ userId: string; paymentId: string }>
      skipped: Array<{ userId: string; reason: string }>
    }>>('/payments/bulk', data)
    return response.data.data!
  }

  async cancelPayment(id: string): Promise<Payment> {
    const response = await this.api.put<ApiResponse<Payment>>(`/payments/${id}/cancel`)
    return response.data.data!
  }

  async markPaymentPaid(id: string, note?: string): Promise<Payment> {
    const response = await this.api.put<ApiResponse<Payment>>(`/payments/${id}/mark-paid`, { note })
    return response.data.data!
  }

  async refundPayment(id: string): Promise<Payment> {
    const response = await this.api.post<ApiResponse<{ payment: Payment; stripeRefundId: string }>>(`/payments/${id}/refund`)
    return response.data.data!.payment
  }

  /**
   * Member self-service: start a Stripe Checkout session and return the
   * URL to redirect to. The browser should `window.location.href = url`.
   */
  async startCheckout(id: string): Promise<{ url: string; sessionId: string }> {
    const response = await this.api.post<ApiResponse<{ url: string; sessionId: string }>>(`/payments/${id}/checkout`)
    return response.data.data!
  }

  // ----- Events -----

  /**
   * List events. Defaults to upcoming + non-canceled. Pass options to widen
   * the window (e.g. for the calendar grid).
   */
  async getEvents(opts: {
    from?: string
    to?: string
    category?: string
    includePast?: boolean
    includeCanceled?: boolean
  } = {}): Promise<AssociationEvent[]> {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.category) params.set('category', opts.category)
    if (opts.includePast) params.set('includePast', 'true')
    if (opts.includeCanceled) params.set('includeCanceled', 'true')
    const qs = params.toString()
    const response = await this.api.get<ApiResponse<AssociationEvent[]>>(
      qs ? `/events?${qs}` : '/events'
    )
    return response.data.data ?? []
  }

  async getEvent(id: string): Promise<AssociationEvent> {
    const response = await this.api.get<ApiResponse<AssociationEvent>>(`/events/${id}`)
    return response.data.data!
  }

  async createEvent(data: CreateEventData): Promise<AssociationEvent> {
    const response = await this.api.post<ApiResponse<AssociationEvent>>('/events', data)
    return response.data.data!
  }

  async updateEvent(id: string, data: Partial<CreateEventData> & { isCanceled?: boolean }): Promise<AssociationEvent> {
    const response = await this.api.put<ApiResponse<AssociationEvent>>(`/events/${id}`, data)
    return response.data.data!
  }

  async deleteEvent(id: string): Promise<void> {
    await this.api.delete(`/events/${id}`)
  }

  /**
   * Set or update the caller's RSVP for an event. Pass `status: 'GOING'`
   * with a `headcount` for the typical "I'm coming and bringing 3" flow.
   */
  async rsvpToEvent(id: string, input: RSVPInput = {}): Promise<EventRSVP> {
    const response = await this.api.put<ApiResponse<EventRSVP>>(`/events/${id}/rsvp`, input)
    return response.data.data!
  }

  async removeRsvp(id: string): Promise<void> {
    await this.api.delete(`/events/${id}/rsvp`)
  }

  // ----- Photos / Album -----

  /**
   * Paginated list of photos. Filters apply server-side; pagination uses an
   * id cursor so infinite scroll feels stable.
   */
  async getPhotos(opts: {
    year?: number
    cottageId?: string
    eventId?: string
    uploadedBy?: string
    limit?: number
    after?: string
  } = {}): Promise<{ photos: Photo[]; nextCursor: string | null }> {
    const params = new URLSearchParams()
    if (opts.year) params.set('year', String(opts.year))
    if (opts.cottageId) params.set('cottageId', opts.cottageId)
    if (opts.eventId) params.set('eventId', opts.eventId)
    if (opts.uploadedBy) params.set('uploadedBy', opts.uploadedBy)
    if (opts.limit) params.set('limit', String(opts.limit))
    if (opts.after) params.set('after', opts.after)
    const qs = params.toString()
    const response = await this.api.get<ApiResponse<{ photos: Photo[]; nextCursor: string | null }>>(
      qs ? `/photos?${qs}` : '/photos'
    )
    return response.data.data!
  }

  /** Calendar years with photo counts, newest first. Powers the year nav. */
  async getPhotoYears(): Promise<PhotoYear[]> {
    const response = await this.api.get<ApiResponse<PhotoYear[]>>('/photos/years')
    return response.data.data ?? []
  }

  async getPhoto(id: string): Promise<Photo> {
    const response = await this.api.get<ApiResponse<Photo>>(`/photos/${id}`)
    return response.data.data!
  }

  /**
   * Upload a single photo. Caller passes the File plus optional metadata.
   * Server-side mediated — the file flows through the backend to Supabase
   * Storage. No client-side bucket setup required beyond what the user has
   * already done for documents.
   */
  async uploadPhoto(
    file: File,
    meta: { caption?: string; takenOn?: string; cottageId?: string; eventId?: string } = {}
  ): Promise<Photo> {
    const formData = new FormData()
    formData.append('file', file)
    if (meta.caption) formData.append('caption', meta.caption)
    if (meta.takenOn) formData.append('takenOn', meta.takenOn)
    if (meta.cottageId) formData.append('cottageId', meta.cottageId)
    if (meta.eventId) formData.append('eventId', meta.eventId)
    const response = await this.api.post<ApiResponse<Photo>>('/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Photo uploads can take a while on slow networks; bump the default 10s.
      timeout: 60_000,
    })
    return response.data.data!
  }

  async updatePhoto(
    id: string,
    data: { caption?: string | null; takenOn?: string | null; cottageId?: string | null; eventId?: string | null }
  ): Promise<Photo> {
    const response = await this.api.put<ApiResponse<Photo>>(`/photos/${id}`, data)
    return response.data.data!
  }

  async deletePhoto(id: string): Promise<void> {
    await this.api.delete(`/photos/${id}`)
  }

  // ----- Direct messages -----

  async getConversations(): Promise<ConversationSummary[]> {
    const response = await this.api.get<ApiResponse<{ conversations: ConversationSummary[] }>>('/messages')
    return response.data.data?.conversations ?? []
  }

  async getConversation(conversationId: string): Promise<ConversationDetail> {
    const response = await this.api.get<ApiResponse<ConversationDetail>>(
      `/messages/${conversationId}`,
    )
    return response.data.data!
  }

  /** Public read of any active member's profile bundle. */
  async getMemberProfile(id: string): Promise<MemberProfile> {
    const response = await this.api.get<ApiResponse<MemberProfile>>(`/members/${id}`)
    if (!response.data.success || !response.data.data) {
      // The backend's errorHandler sends `{ error: { message } }` despite the
      // `ApiResponse.error` type declaring `string` — handle both shapes
      // until that type is corrected project-wide.
      const errField = response.data.error as unknown
      const message =
        typeof errField === 'string'
          ? errField
          : (errField as { message?: string } | null)?.message
      throw new Error(message || 'Failed to load member profile')
    }
    return response.data.data
  }

  /** Find or create a 1:1 conversation with the given recipient. */
  async openConversationWith(recipientId: string): Promise<{ conversationId: string; created: boolean }> {
    const response = await this.api.post<ApiResponse<{ conversationId: string; created: boolean }>>(
      '/messages/conversations',
      { recipientId },
    )
    return response.data.data!
  }

  async sendMessage(conversationId: string, body: string): Promise<MessageItem> {
    const response = await this.api.post<ApiResponse<{ message: MessageItem }>>(
      `/messages/${conversationId}`,
      { body },
    )
    return response.data.data!.message
  }

  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    await this.api.delete(`/messages/${conversationId}/${messageId}`)
  }

  async getUnreadMessageCount(): Promise<number> {
    const response = await this.api.get<ApiResponse<{ unread: number }>>('/messages/unread/count')
    return response.data.data?.unread ?? 0
  }

  // ----- Maintenance Log -----

  /**
   * List maintenance entries. Defaults to non-archived, sorted by status
   * priority (open work first). Pass `cottageId` to scope to one cottage.
   * Pass `status='OPEN,IN_PROGRESS'` to filter to multiple statuses.
   */
  async getMaintenance(opts: {
    cottageId?: string
    status?: string
    category?: string
    authorId?: string
    assigneeId?: string
    includeArchived?: boolean
  } = {}): Promise<MaintenanceEntry[]> {
    const params = new URLSearchParams()
    if (opts.cottageId) params.set('cottageId', opts.cottageId)
    if (opts.status) params.set('status', opts.status)
    if (opts.category) params.set('category', opts.category)
    if (opts.authorId) params.set('authorId', opts.authorId)
    if (opts.assigneeId) params.set('assigneeId', opts.assigneeId)
    if (opts.includeArchived) params.set('includeArchived', 'true')
    const qs = params.toString()
    const response = await this.api.get<ApiResponse<MaintenanceEntry[]>>(
      qs ? `/maintenance?${qs}` : '/maintenance'
    )
    return response.data.data ?? []
  }

  async getMaintenanceCounts(cottageId?: string): Promise<Array<{ cottageId: string; status: string; count: number }>> {
    const qs = cottageId ? `?cottageId=${encodeURIComponent(cottageId)}` : ''
    const response = await this.api.get<ApiResponse<Array<{ cottageId: string; status: string; count: number }>>>(
      `/maintenance/counts${qs}`
    )
    return response.data.data ?? []
  }

  async getMaintenanceEntry(id: string): Promise<MaintenanceEntry> {
    const response = await this.api.get<ApiResponse<MaintenanceEntry>>(`/maintenance/${id}`)
    return response.data.data!
  }

  async createMaintenanceEntry(data: CreateMaintenanceInput): Promise<MaintenanceEntry> {
    const response = await this.api.post<ApiResponse<MaintenanceEntry>>('/maintenance', data)
    return response.data.data!
  }

  async updateMaintenanceEntry(
    id: string,
    data: Partial<CreateMaintenanceInput>
  ): Promise<MaintenanceEntry> {
    const response = await this.api.put<ApiResponse<MaintenanceEntry>>(`/maintenance/${id}`, data)
    return response.data.data!
  }

  /** Sets the caller as assignee + bumps OPEN → IN_PROGRESS. */
  async claimMaintenanceEntry(id: string): Promise<MaintenanceEntry> {
    const response = await this.api.post<ApiResponse<MaintenanceEntry>>(`/maintenance/${id}/claim`)
    return response.data.data!
  }

  async deleteMaintenanceEntry(id: string): Promise<void> {
    await this.api.delete(`/maintenance/${id}`)
  }

  // ----- Lake conditions (NOAA NDBC proxy) -----

  /**
   * Get the latest lake conditions (water temperature + significant wave
   * height) from the nearest reporting NDBC buoy. The backend fetches NDBC
   * server-side (NDBC doesn't allow CORS), fails over to a secondary buoy
   * when the primary is down, and caches results in-memory for 15 minutes.
   */
  async getWaterTemp(buoyId?: string): Promise<{
    fahrenheit: number | null
    celsius: number | null
    waterTempF: number | null
    waterTempC: number | null
    airTempF: number | null
    airTempC: number | null
    waveHeightFt: number | null
    waveHeightM: number | null
    wavePeriodS: number | null
    observedAt: string | null
    stale: boolean
    source: string
  }> {
    const qs = buoyId ? `?buoy=${encodeURIComponent(buoyId)}` : ''
    const response = await this.api.get<ApiResponse<{
      fahrenheit: number | null
      celsius: number | null
      waterTempF: number | null
      waterTempC: number | null
      airTempF: number | null
      airTempC: number | null
      waveHeightFt: number | null
      waveHeightM: number | null
      wavePeriodS: number | null
      observedAt: string | null
      stale: boolean
      source: string
    }>>(`/lake/water-temp${qs}`)
    return response.data.data!
  }

  // ----- Notification preferences + Web Push -----

  async getNotificationPreferences(): Promise<{
    bulletin: boolean; events: boolean; messages: boolean; payments: boolean
  }> {
    const r = await this.api.get<ApiResponse<{ bulletin: boolean; events: boolean; messages: boolean; payments: boolean }>>(
      '/notifications/preferences',
    )
    return r.data.data!
  }

  async updateNotificationPreferences(
    prefs: Partial<{ bulletin: boolean; events: boolean; messages: boolean; payments: boolean }>,
  ): Promise<{ bulletin: boolean; events: boolean; messages: boolean; payments: boolean }> {
    const r = await this.api.put<ApiResponse<{ bulletin: boolean; events: boolean; messages: boolean; payments: boolean }>>(
      '/notifications/preferences', prefs,
    )
    return r.data.data!
  }

  async getVapidPublicKey(): Promise<string | null> {
    const r = await this.api.get<ApiResponse<{ publicKey: string | null }>>('/notifications/push/public-key')
    return r.data.data?.publicKey ?? null
  }

  async subscribePush(sub: { endpoint: string; keys: { p256dh: string; auth: string }; deviceLabel?: string }): Promise<void> {
    await this.api.post('/notifications/push/subscribe', sub)
  }

  async unsubscribePush(endpoint: string): Promise<void> {
    await this.api.post('/notifications/push/unsubscribe', { endpoint })
  }

  // Notification methods. Controllers wrap in `{ success, data, meta }`;
  // unwrap so callers always get a typed Notification (and surface the
  // server-side `unreadCount` hint where useful).

  async getNotifications(): Promise<Notification[]> {
    const response = await this.api.get<ApiResponse<Notification[]>>('/notifications')
    return response.data.data ?? []
  }

  async getNotificationsWithMeta(): Promise<{ notifications: Notification[]; unreadCount: number }> {
    const response = await this.api.get<ApiResponse<Notification[]> & { meta?: { unreadCount: number } }>(
      '/notifications'
    )
    return {
      notifications: response.data.data ?? [],
      unreadCount: response.data.meta?.unreadCount ?? 0,
    }
  }

  async markNotificationAsRead(id: string): Promise<Notification> {
    const response = await this.api.put<ApiResponse<Notification>>(`/notifications/${id}/read`)
    return response.data.data!
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.api.put('/notifications/read-all')
  }

  async deleteNotification(id: string): Promise<void> {
    await this.api.delete(`/notifications/${id}`)
  }

  // ----- Help Desk (AI assistant) -----

  /**
   * Send the conversation history to the secure backend Help Desk endpoint and
   * get the assistant's reply. The backend chooses the provider (Claude/OpenAI)
   * from server-side env keys — no API key ever touches the browser.
   *
   * Uses a 90s timeout because model inference + live web search is far slower
   * than a normal CRUD call (and the backend can cold-start on top of that).
   */
  async helpDeskChat(
    messages: HelpDeskChatMessage[],
    meta: { route?: string; page?: string } = {},
    signal?: AbortSignal,
  ): Promise<HelpDeskReply> {
    const response = await this.api.post<ApiResponse<HelpDeskReply>>(
      '/help-desk/chat',
      { messages, ...meta },
      { timeout: 90_000, ...(signal ? { signal } : {}) },
    )
    return response.data.data!
  }

  // ----- Family invites -----

  /** Create an invite tied to the logged-in member's cottage. */
  async createFamilyInvite(input: {
    firstName: string
    lastName: string
    email: string
    relationship: FamilyRelationship
    message?: string
  }): Promise<FamilyInvite> {
    const r = await this.api.post<ApiResponse<FamilyInvite>>('/family-invites', input)
    return r.data.data!
  }

  /** All invites for the member's cottage (pending + accepted + past). */
  async getFamilyInvites(): Promise<FamilyInvite[]> {
    const r = await this.api.get<ApiResponse<FamilyInvite[]>>('/family-invites')
    return r.data.data ?? []
  }

  async revokeFamilyInvite(id: string): Promise<FamilyInvite> {
    const r = await this.api.post<ApiResponse<FamilyInvite>>(`/family-invites/${id}/revoke`)
    return r.data.data!
  }

  /** Public: validate an invite token and fetch safe details. */
  async getFamilyInviteByToken(token: string): Promise<PublicFamilyInvite> {
    const r = await this.api.get<ApiResponse<PublicFamilyInvite>>(
      `/family-invite/${encodeURIComponent(token)}`,
    )
    return r.data.data!
  }

  /** Public: accept an invite (password required only for brand-new accounts). */
  async acceptFamilyInvite(token: string, password?: string): Promise<AcceptInviteResult> {
    const r = await this.api.post<ApiResponse<AcceptInviteResult>>(
      `/family-invite/${encodeURIComponent(token)}/accept`,
      password ? { password } : {},
    )
    return r.data.data!
  }

  // Token management - Get from Supabase session via auth store
  private getAccessToken(): string | null {
    const { accessToken } = useAuthStore.getState()
    return accessToken
  }

  async logout(): Promise<void> {
    // Sign-out is owned by the auth store / Supabase. Kept for backward compat.
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  // Utility methods
  isAuthenticated(): boolean {
    const { isAuthenticated } = useAuthStore.getState()
    return isAuthenticated
  }

  getBaseURL(): string {
    return this.baseURL
  }
}

// Create singleton instance
export const apiService = new ApiService()
